// frontend/src/components/ConsultationDetailModal.js
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faCopy, faShareNodes, faFilePdf, faFileLines, faCheck } from '@fortawesome/free-solid-svg-icons';
import { handleShareAsTxt, handleShareAsPdf, convertMarkdownToPlainText } from './ShareComponent';
import { useNotification } from '../contexts/NotificationContext';
import styles from './ConsultationDetailModal.module.css';

// Componente para uma seção retrátil
const CollapsibleSection = ({ title, content, isExpanded, onToggle, onCopy, onShare, shareMenu, setShareMenu, sectionKey, addNotification, t, i18n, actionSuccess, setActionSuccess }) => {
  const shareMenuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target) && !event.target.closest(`[data-share-button='${sectionKey}']`)) {
        setShareMenu({ open: false, target: null });
      }
    }
    if (shareMenu.open && shareMenu.target === sectionKey) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [shareMenu, sectionKey, setShareMenu]);
  
  const handleShareAction = async (shareFn) => {
    setShareMenu({ open: false, target: null });
    const success = await shareFn();
    if (success) {
      setActionSuccess({ target: sectionKey, type: 'share' });
      setTimeout(() => setActionSuccess({ target: null, type: null }), 2000);
    }
  };

  return (
    <div className={styles.notesSection}>
      <div className={styles.sectionHeader} onClick={onToggle}>
        <h4>{title}</h4>
        <div className={styles.headerActions} onClick={(e) => e.stopPropagation()}>
          <button onClick={onCopy} title={t('copy')}>
            {actionSuccess.target === sectionKey && actionSuccess.type === 'copy' ? <FontAwesomeIcon icon={faCheck} className={styles.successIcon} /> : <FontAwesomeIcon icon={faCopy} />}
          </button>
          <div style={{ position: 'relative' }}>
             <button onClick={onShare} title={t('share')} data-share-button={sectionKey}>
                {actionSuccess.target === sectionKey && actionSuccess.type === 'share' ? <FontAwesomeIcon icon={faCheck} className={styles.successIcon} /> : <FontAwesomeIcon icon={faShareNodes} />}
             </button>
             {shareMenu.open && shareMenu.target === sectionKey && (
                <div ref={shareMenuRef} className={styles.shareMenu}>
                  <button onClick={() => handleShareAction(() => handleShareAsTxt(content, title, t, addNotification))}>
                    <FontAwesomeIcon icon={faFileLines} /> {t('shareAsTxt')}
                  </button>
                  <button onClick={() => handleShareAction(() => handleShareAsPdf(content, addNotification, i18n))}>
                    <FontAwesomeIcon icon={faFilePdf} /> {t('shareAsPdf')}
                  </button>
                </div>
             )}
          </div>
          <button onClick={onToggle} title={isExpanded ? t('collapse') : t('expand')}>
            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
          </button>
        </div>
      </div>
      <div className={`${styles.collapsibleContent} ${isExpanded ? styles.expanded : ''}`}>
        <div className={styles.markdownContent}>
            <ReactMarkdown rehypePlugins={[rehypeSanitize]}>
            {content || `*${t('notAvailable')}*`}
            </ReactMarkdown>
        </div>
      </div>
    </div>
  );
};


const ConsultationDetailModal = ({ isOpen, onClose, consultation }) => {
  const { t, i18n } = useTranslation();
  const { addNotification } = useNotification();
  const [expandedSections, setExpandedSections] = useState({
    original: true,
    improved: true,
    summary: true,
  });
  const [shareMenu, setShareMenu] = useState({ open: false, target: null });
  const [actionSuccess, setActionSuccess] = useState({ target: null, type: null });

  if (!isOpen || !consultation) {
    return null;
  }

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleCopy = (markdownText, sectionKey) => {
    if (!markdownText) return;
    const plainText = convertMarkdownToPlainText(markdownText);
    navigator.clipboard.writeText(plainText)
      .then(() => {
        setActionSuccess({ target: sectionKey, type: 'copy' });
        setTimeout(() => setActionSuccess({ target: null, type: null }), 2000);
      })
      .catch(err => {
        console.error("Erro ao copiar:", err);
        addNotification(t('errorCopying'), 'error');
      });
  };

  const handleShareClick = (e, target) => {
    e.stopPropagation();
    setShareMenu(prev => ({
      open: prev.target === target ? !prev.open : true,
      target: target,
    }));
  };

  const modalTitle = `${t('specialty')}: ${t(consultation.specialty)} - ${new Date(consultation.created_at).toLocaleDateString()}`;

  return ReactDOM.createPortal(
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>{modalTitle}</h3>
          <button onClick={onClose} className={styles.closeButton}>&times;</button>
        </div>
        <div className={styles.modalBody}>

          <CollapsibleSection
            title={t('originalConsultation')}
            content={consultation.raw_notes}
            isExpanded={expandedSections.original}
            onToggle={() => toggleSection('original')}
            onCopy={() => handleCopy(consultation.raw_notes, 'original')}
            onShare={(e) => handleShareClick(e, 'original')}
            shareMenu={shareMenu}
            setShareMenu={setShareMenu}
            sectionKey="original"
            addNotification={addNotification}
            t={t}
            i18n={i18n}
            actionSuccess={actionSuccess}
            setActionSuccess={setActionSuccess}
          />

          <CollapsibleSection
            title={t('improvedConsultation')}
            content={consultation.improved_notes}
            isExpanded={expandedSections.improved}
            onToggle={() => toggleSection('improved')}
            onCopy={() => handleCopy(consultation.improved_notes, 'improved')}
            onShare={(e) => handleShareClick(e, 'improved')}
            shareMenu={shareMenu}
            setShareMenu={setShareMenu}
            sectionKey="improved"
            addNotification={addNotification}
            t={t}
            i18n={i18n}
            actionSuccess={actionSuccess}
            setActionSuccess={setActionSuccess}
          />

          <CollapsibleSection
            title={t('summary')}
            content={consultation.summary}
            isExpanded={expandedSections.summary}
            onToggle={() => toggleSection('summary')}
            onCopy={() => handleCopy(consultation.summary, 'summary')}
            onShare={(e) => handleShareClick(e, 'summary')}
            shareMenu={shareMenu}
            setShareMenu={setShareMenu}
            sectionKey="summary"
            addNotification={addNotification}
            t={t}
            i18n={i18n}
            actionSuccess={actionSuccess}
            setActionSuccess={setActionSuccess}
          />
        </div>
      </div>
    </div>,
    document.getElementById('consultation-detail-modal-portal')
  );
};

export default ConsultationDetailModal;